import axios from "axios";
import { decodeToken } from "@okta/okta-auth-js";
// import OktaJwtVerifier from '@okta/jwt-verifier';
import { AuthToken } from "../entities/User";
import {
  getCxpProfile,
  getUserProfileFromCxp,
} from "../services/CxpProfileService";
import { ProgramContainer } from "../entities/formTemplateDataType";
import { AUTH_TOKENS, USER_PROFILE } from "../entities/Constants";
import { jwtDecode } from "jwt-decode";
import DataUtils from "../utilities/DataUtils";
import { se } from "date-fns/locale";
import { getEnvVar } from "../config/RuntimeConfig";
import { getPublicSecurePage } from "../utilities/ElementUtils";
import {
  buildSectionUrl,
  normalizeCxpRedirectPath,
} from "../utilities/URLUtils";

const MaxTry = 3;
const SIGNIN_ATTEMPT_PREFIX = "signinAttempts_";
const PS_SIGNIN_IN_FLIGHT = "ps-signin-in-flight";

const getSigninAttemptKey = (appId: string) =>
  `${SIGNIN_ATTEMPT_PREFIX}${(appId || "").toLowerCase()}`;

const incrementSigninAttempts = (appId: string): boolean => {
  try {
    const key = getSigninAttemptKey(appId);
    const raw = sessionStorage.getItem(key);
    const current = raw ? parseInt(raw, 10) || 0 : 0;
    if (current >= MaxTry) {
      console.error(
        `Max sign-in attempts reached for ${appId}, aborting to avoid redirect loop.`,
      );
      return false;
    }
    sessionStorage.setItem(key, (current + 1).toString());
    return true;
  } catch {
    // If sessionStorage is not available, allow the sign-in attempt rather than looping.
    return true;
  }
};

const resetSigninAttempts = (appId: string) => {
  try {
    sessionStorage.removeItem(getSigninAttemptKey(appId));
    sessionStorage.removeItem(PS_SIGNIN_IN_FLIGHT);
  } catch {
    // Ignore storage errors on cleanup
  }
};

export const signin = async (appId: string, isEng: boolean) => {
  // Prevent double redirects in the same browser session.
  if (sessionStorage.getItem(PS_SIGNIN_IN_FLIGHT) === "1") {
    return;
  }
  if (!incrementSigninAttempts(appId)) {
    return;
  }
  sessionStorage.setItem(PS_SIGNIN_IN_FLIGHT, "1");

  // Set flag in localStorage to advance past public secure section after sign-in
  // This flag will be checked when the user returns from authentication
  // localStorage persists across domain navigations unlike sessionStorage
  const flagData = {
    value: true,
    timestamp: Date.now(),
    expiresIn: 5 * 60 * 1000, // 5 minutes
  };
  console.log(
    "[signin] Setting advanceAfterCxpReturn flag before authentication:",
    flagData,
  );
  localStorage.setItem("advanceAfterCxpReturn", JSON.stringify(flagData));

  // Store previousUrl for page tracking after OAuth return.
  // Prefer the last in-form previousUrl (e.g., instructions) so skipped sections
  // after return do not become referrer context.
  // Use localStorage since sessionStorage may be reset across domain navigation.
  const previousUrlForOAuthTracking =
    sessionStorage.getItem("previousUrl") || window.location.href;
  console.log(
    "[signin] Storing previousUrl for post-OAuth tracking:",
    previousUrlForOAuthTracking,
  );
  localStorage.setItem("previousUrlBeforeOAuth", previousUrlForOAuthTracking);

  const issuer = getEnvVar("PUBLIC_SECURE_ISSUER");
  const client_id = getEnvVar("PUBLIC_SECURE_CLIENTID");

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const stateValue = generateState(appId, isEng);

  let redirect_uri = getEnvVar("REDIRECT_URL");

  if (!redirect_uri.includes(`${appId}`)) {
    if (stateValue.endsWith("EN")) redirect_uri = redirect_uri + "/" + appId;
    else redirect_uri = redirect_uri + "/fr/" + appId;
  }
  // if(isEng){
  //     sessionStorage.setItem("lang", isEng.toString());
  // } else {
  //     sessionStorage.setItem("lang", "false");
  // }

  const params = new URLSearchParams({
    client_id: client_id,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: redirect_uri,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state: stateValue,
  });

  let authUrl = `${issuer}/v1/authorize?${params.toString()}`;
  isEng = sessionStorage.getItem("stateValue")?.endsWith("EN") ? true : false;
  if (!isEng) {
    authUrl = authUrl + "&lang=fr";
  }
  // console.log(authUrl);
  // sessionStorage.removeItem(AUTH_TOKENS);
  // sessionStorage.removeItem(USER_PROFILE);
  window.location.href = authUrl;

  const urlParams = new URLSearchParams(window.location.search);
  const returnedState = urlParams.get("state");
  if (returnedState !== sessionStorage.getItem("stateValue")) {
    throw new Error("Invalid state: possible CSRF attack");
  }
};

export const generateState = (appId: string, isEng: boolean) => {
  const state = sessionStorage.getItem("stateValue");
  if (state && (state.endsWith("EN") || state.endsWith("FR"))) {
    return state;
  }

  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  const stateValue =
    Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("") +
    appId +
    (isEng ? "EN" : "FR");

  sessionStorage.setItem("stateValue", stateValue);

  return stateValue;
};

export const getTokens = async (
  programContainer: ProgramContainer,
): Promise<AuthToken | null> => {
  const code = new URL(window.location.href).searchParams.get("code");
  const tokens = sessionStorage.getItem(AUTH_TOKENS);
  const lang = sessionStorage.getItem("stateValue")?.endsWith("EN")
    ? true
    : false;
  try {
    const verifier = sessionStorage.getItem("codeVerifier");
    console.log("verifer", verifier);

    if (!verifier) {
      signin(programContainer.CategoryId, lang);
    }
  } catch (error) {
    signin(programContainer.CategoryId, lang);
  }

  if (!code && !tokens) {
    return null;
  }

  let token = null;
  if (tokens) {
    console.log("parse tokens: ", tokens);
    token = JSON.parse(tokens);
    // Returning with tokens means the redirect cycle completed; clear guard.
    sessionStorage.removeItem(PS_SIGNIN_IN_FLIGHT);
  }

  try {
    if (!token) {
      let redirect_Uri = getEnvVar("REDIRECT_URL");

      if (!redirect_Uri.includes(`${programContainer.CategoryId}`)) {
        const isEng = sessionStorage.getItem("stateValue")?.endsWith("EN")
          ? true
          : false;
        if (isEng) {
          redirect_Uri = redirect_Uri + "/" + programContainer.CategoryId;
        } else {
          redirect_Uri = redirect_Uri + "/fr/" + programContainer.CategoryId;
        }
      }
      const response = await axios.post<AuthToken>(
        `${getEnvVar("LAYOUT_API_URL")}/data/auth/token`,
        {
          code,
          code_verifier: generateCodeVerifier(),
          redirectUri: redirect_Uri,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status !== 200) {
        return null;
      }
      const data = response.data;
      sessionStorage.setItem(AUTH_TOKENS, JSON.stringify(data));
      resetSigninAttempts(programContainer.CategoryId);
      // sessionStorage.removeItem("codeVerifier");
      console.log("Tokens:", data);
      token = data;
      sessionStorage.removeItem(PS_SIGNIN_IN_FLIGHT);
    }
    return token;
  } catch (error) {
    console.error(
      "Token Exchange Error:",
      error.response?.data || error.message,
    );
    return null;
  }
};

export const checkProfileAccount = async (
  programContainer: ProgramContainer,
  access_token?: AuthToken,
) => {
  let authTokens = access_token;
  const lang = sessionStorage.getItem("stateValue")?.endsWith("EN")
    ? true
    : false;
  if (!access_token) {
    authTokens = await getTokens(programContainer);
    if (!authTokens?.access_token) {
      signin(programContainer.CategoryId, lang);
    }
  } else if (!verifyOktaToken(authTokens.access_token)) {
    signin(programContainer.CategoryId, lang);
  }

  try {
    const { payload } = decodeToken(authTokens.idToken || authTokens.id_token);
    console.log("decode token:", payload.sub);
    let profile = {
      ...programContainer.Profile,
      token: authTokens.access_token,
      userId: payload.sub,
      email: payload.email,
    };
    const cxpProfile = await getCxpProfile(authTokens.access_token);

    if (!cxpProfile?.details) {
      console.log("User does not have cxp account");
      const categoryId = programContainer.CategoryId;
      // window.location.replace(`${process.env.PUBLIC_CXP_WEB_BASE_PATH}/auth/signin?flow=${categoryId}`);
      let flow = categoryId;

      // Check if there's a stashId - only redirect to drafts in that case
      const urlParams = new URLSearchParams(window.location.search);
      const stashId =
        urlParams.get("stashId") || sessionStorage.getItem("pendingStashId");
      console.log("[checkProfileAccount] stashId:", stashId);

      let signinUrl = `${getEnvVar("PUBLIC_CXP_WEB_BASE_PATH")}/${lang ? "en" : "fr"}/auth/signin?flow=${flow}`;

      if (stashId) {
        // Only redirect to drafts if there's a stashId (draft save scenario)
        console.log("[checkProfileAccount] Has stashId, redirecting to drafts");
        const redirectUri = `/web/drafts?stashId=${encodeURIComponent(stashId)}`;
        signinUrl += `&redirectUri=${encodeURIComponent(redirectUri)}`;
      } else {
        // Set flag in localStorage to advance past public secure section after returning from CXP
        // localStorage persists across domain navigations unlike sessionStorage
        // Store with timestamp for expiration (5 minutes)
        const flagData = {
          value: true,
          timestamp: Date.now(),
          expiresIn: 5 * 60 * 1000, // 5 minutes
        };
        console.log(
          "[checkProfileAccount] Setting advanceAfterCxpReturn flag:",
          flagData,
        );
        localStorage.setItem("advanceAfterCxpReturn", JSON.stringify(flagData));
        localStorage.setItem("forceTrackAfterCxpRegistration", "true");
        console.log(
          "[checkProfileAccount] Flag set, verification:",
          localStorage.getItem("advanceAfterCxpReturn"),
        );

        // Find the Public Secure section and the next visible section after it
        const publicSecureSection = getPublicSecurePage(
          programContainer.ListOfFormSections,
        );
        let targetSection = null;

        if (publicSecureSection) {
          const publicSecureIndex =
            programContainer.ListOfFormSections.indexOf(publicSecureSection);
          console.log(
            "[checkProfileAccount] Public Secure section index:",
            publicSecureIndex,
          );
          const hasAcceptedTerms = !!(
            programContainer.Profile?.agreedToTermsOfUseOn ||
            (programContainer as any)?.Entity?.accepted === true
          );

          // Find the next visible section after Public Secure.
          // Skip onboarding instructions always, and skip other SkipPageViewTracking
          // sections only when user already accepted Terms of Use.
          for (
            let i = publicSecureIndex + 1;
            i < programContainer.ListOfFormSections.length;
            i++
          ) {
            const section = programContainer.ListOfFormSections[i];

            // Skip hidden sections
            if (section?.Visible === false) {
              continue;
            }

            const sectionSlug = (section?.pageName || section?.ENUTitle || "")
              .toString()
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");

            const shouldSkipSection =
              sectionSlug === "instructions" ||
              (section?.SkipPageViewTracking && hasAcceptedTerms);

            if (shouldSkipSection) {
              console.log(
                "[checkProfileAccount] Skipping section at index",
                i,
                `("${section.ENUTitle}") - skipped by onboarding rules; hasAcceptedTerms=${hasAcceptedTerms}`,
              );
              continue;
            }

            // Found a valid target section
            targetSection = section;
            console.log(
              "[checkProfileAccount] Target section found at index",
              i,
              ":",
              section.ENUTitle,
            );
            break;
          }
        }

        // Construct the redirect URI - use the target section URL if found, otherwise fall back to current path
        let redirectPath;
        if (targetSection) {
          const targetUrl = buildSectionUrl(
            programContainer,
            targetSection,
            getEnvVar("IS_RUNTIME"),
          );
          if (targetUrl) {
            // Extract just the path and search params from the full URL
            try {
              const url = new URL(targetUrl);
              redirectPath = url.pathname + url.search;
              console.log(
                "[checkProfileAccount] Constructed target path:",
                redirectPath,
              );
            } catch (e) {
              console.warn(
                "[checkProfileAccount] Failed to parse target URL, using fallback",
              );
              redirectPath = window.location.pathname + window.location.search;
            }
          } else {
            redirectPath = window.location.pathname + window.location.search;
          }
        } else {
          // Fallback to current path if no target section found
          redirectPath = window.location.pathname + window.location.search;
        }

        // Never propagate OAuth callback params into cxp-ui redirectUri.
        // Otherwise users can return with stale code/state still on the URL.
        try {
          const sanitizedRedirectUrl = new URL(
            redirectPath,
            window.location.origin,
          );
          sanitizedRedirectUrl.searchParams.delete("code");
          sanitizedRedirectUrl.searchParams.delete("state");
          redirectPath =
            sanitizedRedirectUrl.pathname +
            sanitizedRedirectUrl.search +
            (sanitizedRedirectUrl.hash || "");
        } catch {
          // Keep original redirectPath if URL parsing fails.
        }

        redirectPath = normalizeCxpRedirectPath(redirectPath);

        console.log(
          "[checkProfileAccount] Final redirect path for CXP:",
          redirectPath,
        );
        signinUrl += `&redirectUri=${encodeURIComponent(redirectPath)}`;
      }

      localStorage.setItem("authReturnSource", "cxpUi");
      console.log("[checkProfileAccount] Redirecting to CXP:", signinUrl);
      window.location.replace(signinUrl);
    } else {
      console.log("User has cxp account");
      const userProfile = getUserProfileFromCxp(
        cxpProfile,
        authTokens.access_token,
        payload.sub,
        payload.email,
        profile,
      );
      //sessionStorage.setItem(USER_PROFILE, JSON.stringify(userProfile));
      programContainer.Profile = userProfile;
      sessionStorage.setItem(USER_PROFILE, JSON.stringify(userProfile));
      console.log("profile after fetch cxp profile:", programContainer);
    }
  } catch (error) {
    console.error(
      "Error decode token or fetching CXP profile:",
      error.response?.data || error.message,
    );
  }
  return programContainer;
  // }, 10000 ));
};

/**
 * Retrieves and validates the 'advanceAfterCxpReturn' flag from localStorage.
 * Returns true if the flag exists, is valid, and hasn't expired.
 * Note: This function does NOT remove the flag - use consumeAdvanceFlag() to remove it after use.
 */
export const getAndValidateAdvanceFlag = (): boolean => {
  try {
    const stored = localStorage.getItem("advanceAfterCxpReturn");
    console.log(
      "[getAndValidateAdvanceFlag] Checking flag, stored value:",
      stored,
    );

    // Handle null case - flag doesn't exist
    if (stored === null) {
      console.log("[getAndValidateAdvanceFlag] Flag is null, returning false");
      return false;
    }

    // Parse the stored JSON data
    const flagData = JSON.parse(stored);
    console.log("[getAndValidateAdvanceFlag] Parsed flag data:", flagData);

    // Validate the structure and check expiration
    if (
      flagData &&
      flagData.value &&
      flagData.timestamp &&
      flagData.expiresIn
    ) {
      const now = Date.now();
      const expirationTime = flagData.timestamp + flagData.expiresIn;
      const age = now - flagData.timestamp;

      console.log(
        "[getAndValidateAdvanceFlag] Flag age:",
        age,
        "ms, expires in:",
        flagData.expiresIn,
        "ms",
      );

      // Check if the flag has expired
      if (now < expirationTime) {
        console.log(
          "[getAndValidateAdvanceFlag] Flag is VALID, returning true",
        );
        return true;
      } else {
        console.log(
          "[getAndValidateAdvanceFlag] Flag has EXPIRED, removing it",
        );
        localStorage.removeItem("advanceAfterCxpReturn");
        return false;
      }
    }

    // Invalid structure - clean up and return false
    console.log(
      "[getAndValidateAdvanceFlag] Invalid flag structure, removing it",
    );
    localStorage.removeItem("advanceAfterCxpReturn");
    return false;
  } catch (error) {
    // Handle parsing errors or storage access issues
    console.error("Error validating advanceAfterCxpReturn flag:", error);
    try {
      localStorage.removeItem("advanceAfterCxpReturn");
    } catch {
      // Ignore cleanup errors
    }
    return false;
  }
};

/**
 * Removes the 'advanceAfterCxpReturn' flag from localStorage.
 * Call this after successfully using the flag.
 */
export const consumeAdvanceFlag = (): void => {
  localStorage.removeItem("advanceAfterCxpReturn");
  console.log("[consumeAdvanceFlag] Flag removed from localStorage");
};

export const valifyToken = (token): boolean => {
  try {
    const decoded = jwtDecode(token);
    console.log("Decoded token:", decoded);

    // Validate claims
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    if (decoded.exp < now) {
      console.error("Token has expired");
    } else {
      return true;
    }
  } catch (err) {
    console.error("Invalid token:", err.message);
  }

  return false;
};

export const generateCodeVerifier = () => {
  if (sessionStorage.getItem("codeVerifier")) {
    return sessionStorage.getItem("codeVerifier");
  }

  const array = new Uint8Array(32); // Create a 32-byte array
  window.crypto.getRandomValues(array); // Fill it with random values
  const codeVerifier = btoa(
    String.fromCharCode(...Array.from(array)), // Convert to Base64
  )
    .replace(/\+/g, "-") // Replace `+` with `-`
    .replace(/\//g, "_") // Replace `/` with `_`
    .replace(/=+$/, ""); // Remove `=` padding

  sessionStorage.setItem("codeVerifier", codeVerifier);

  return codeVerifier;
};

export const generateCodeChallenge = async (verifier) => {
  // if(sessionStorage.getItem("codeChallenge")){
  //     return sessionStorage.getItem("codeChallenge");
  // } else {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data); // Generate SHA-256 hash
  const codeChallenge = btoa(
    String.fromCharCode(...Array.from(new Uint8Array(digest))),
  ) // Convert to Base64
    .replace(/\+/g, "-") // Replace `+` with `-`
    .replace(/\//g, "_") // Replace `/` with `_`
    .replace(/=+$/, ""); // Remove `=` padding
  return codeChallenge;
  // }
};

export const fetchSessionFromServer = async (
  programContainer: ProgramContainer,
) => {
  try {
    const response = await fetch(
      `${getEnvVar("PUBLIC_CXP_WEB_BASE_PATH")}/api/auth/session`,
      {
        credentials: "include", // Ensure cookies are sent with the request
      },
    );

    if (response.ok) {
      const data = await response.json();
      // sessionStorage.setItem(AUTH_TOKENS, JSON.stringify(data));
      // Only update the token if we actually received one — avoid overwriting
      // an existing token (e.g. a PS token set by syncProfileToken) with undefined.
      if (data?.accessToken) {
        const profile = {
          ...programContainer.Profile,
          token: data.accessToken,
        };
        programContainer.Profile = profile;
      }
      // sessionStorage.setItem(USER_PROFILE, JSON.stringify(profile));
      console.log("Session Data:", data);
      console.log(programContainer);
    } else {
      console.error("Failed to fetch session");
    }
  } catch (ex) {}

  return programContainer;
};

export const clearSessionStorage = () => {
  sessionStorage.removeItem(AUTH_TOKENS);
  sessionStorage.removeItem(USER_PROFILE);
  sessionStorage.removeItem("codeVerifier");
  sessionStorage.removeItem("codeChallenge");
  sessionStorage.removeItem("stateValue");
  try {
    const keys = Object.keys(sessionStorage);
    keys
      .filter((key) => key.indexOf(SIGNIN_ATTEMPT_PREFIX) === 0)
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore storage errors during cleanup
  }
};

export const cleanup = (app: string, programContainer: ProgramContainer) => {
  clearSessionStorage();
  localStorage.removeItem("userProfile");
  DataUtils.clearFormValue(programContainer);
};

export const cleanSession = (
  app: string,
  profile: any,
  lang: "en" | "fr" = "en",
  customRedirectURL?: string,
) => {
  clearSessionStorage();

  if (customRedirectURL) {
    window.location.replace(customRedirectURL);
    return;
  }

  if (profile?.token) {
    const url =
      getEnvVar("PUBLIC_CXP_WEB_BASE_PATH") +
      "/auth/signin?redirectUri=" +
      (lang === "en" ? "/web/logout/" : "/web/fr/logout/") +
      app.toLowerCase();
    window.location.replace(url);
  } else if (profile) {
    window.location.replace(
      lang === "en" ? getEnvVar("CXP_URL") : getEnvVar("CXP_URL_F"),
    );
  } else {
    window.location.replace(
      lang === "en"
        ? getEnvVar("SERVICEONTARIO_URL")
        : getEnvVar("SERVICEONTARIO_URL_F"),
    );
  }
};

// const oktaJwtVerifier = new OktaJwtVerifier({
//     issuer: process.env.PUBLIC_SECURE_ISSUER,
//     clientId: process.env.PUBLIC_SECURE_CLIENTID,
//   });

// export const verifyOktaToken = async (token: string) => {
//     try {
//         const { payload } = decodeToken(token);
//         const jwt = await oktaJwtVerifier.verifyAccessToken(token, payload.aud);
//         console.log('Token is valid:', jwt.claims);
//         return !jwt.isExpired();
//       } catch (err) {
//         console.error('Token verification failed:', err.message);
//       }
//       return false;
// }

const verifyOktaToken = (token: string) => {
  try {
    const { payload } = decodeToken(token);
    console.log("Token is valid:", payload);
    const deadLine = Math.floor(Date.now() / 1000) + 300;
    if (payload.exp < deadLine) {
      console.log("Token has expired");
      return false;
    }
  } catch (error) {
    console.error("Token verification failed:", error.message);
    return false;
  }
  return true;
};
